import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { saveAs } from 'file-saver';
import { Context as ReactContext } from 'react';
import * as React from 'react';

import * as Styled from '../../Common/Styles/viewRequest.styled';
import { PrivacyApprovalArtifact, RetrieveAttachmentRequest, RetrieveAttachmentResponse } from '../CommonModels/Attachments';
import { getTruncatedDisplayFileName } from '../Helper/attachmentFileNameHelper';

type ViewPrivacyApprovalArtifactsProps = {
  attachmentList: Array<PrivacyApprovalArtifact>;
};

export function ViewPrivacyApprovalArtifacts(props: ViewPrivacyApprovalArtifactsProps): React.ReactElement {
  const { attachmentList } = props;
  const { httpClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);

  const retrieveAttachments = async (retrieveAttachmentRequestList: RetrieveAttachmentRequest[]) => {
    return new Promise((resolve) => {
      httpClient
        .post(`${__API_BASE_URL__}/common/retrieveAttachments`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
          data: retrieveAttachmentRequestList,
        })
        .then(({ data }) => {
          resolve(data as RetrieveAttachmentResponse[]);
        })
        .catch(() => {
          resolve(null);
        });
    });
  };

  const signatures: Record<string, string> = {
    JVBERi0: 'application/pdf',
    R0lGODdh: 'image/gif',
    R0lGODlh: 'image/gif',
    iVBORw0KGgo: 'image/png',
    '/9j/': 'image/jpg',
  };

  const getMimeType = (base64: string) => {
      for (const sign in signatures) {
        if (base64.startsWith(sign)) {
          return signatures[sign];
        }
      }
      return null;
  };

  const convertBase64ToBlob = (base64String: string, contentType: string, sliceSize: number) => {
    const byteCharacters = atob(base64String);
    const byteArrays = [];
 
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
 
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
 
        const byteArray = new Uint8Array(byteNumbers);
 
        byteArrays.push(byteArray);
    }
 
    const blob = new Blob(byteArrays, {type: contentType});
    return blob;
  }

  const handleOnClickDownload = async (privacyApprovalArtifact: PrivacyApprovalArtifact) => {

    const folderName: string = privacyApprovalArtifact.folderName;
    const fileName: string = privacyApprovalArtifact.fileName;

    const retrieveAttachmentRequestList: RetrieveAttachmentRequest[] = [
      {
        folderName: folderName,
        fileName: fileName,
      }
    ];

    const retrieveAttachmentResponseList : RetrieveAttachmentResponse[]  = 
      (await retrieveAttachments(retrieveAttachmentRequestList)) as RetrieveAttachmentResponse[];

    if (retrieveAttachmentResponseList != null && 
        retrieveAttachmentResponseList[0].fileDataBase64 != null) {

      const base64String: string = retrieveAttachmentResponseList[0].fileDataBase64;
      const mimeType: string = getMimeType(base64String);
      const blob: Blob = convertBase64ToBlob(base64String, mimeType, 512)
      await saveAs(blob, fileName);
    }
  };

  const getDisplayFileName = (item: PrivacyApprovalArtifact): string => {
    const maxFileNameLength = 25;
    return getTruncatedDisplayFileName(item.fileName, maxFileNameLength);
  };

  const onRenderAttachmentItem = (item: PrivacyApprovalArtifact) => {
    const displayFileName = getDisplayFileName(item);
    
    return (
      <Styled.AttachmentText
        aria-label="Download Attachment"
        onClick={() => {
          handleOnClickDownload(item);
        }}
      >
        {displayFileName}
      </Styled.AttachmentText>
    );
  };

  return (
    <Styled.AttachmentsListContainer data-testid="attachments">

      {attachmentList.length > 0 && attachmentList.map((attachment: PrivacyApprovalArtifact) => (
        onRenderAttachmentItem(attachment)
      ))}

    </Styled.AttachmentsListContainer>
  );
}
